import { useState, useEffect, useMemo, useRef, useLayoutEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';

import { X, Plug, CheckCircle2, Search, Filter } from 'lucide-react';
import toast from 'react-hot-toast';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useResponsive } from '@/hooks/useResponsive';
import { cn } from '@/lib/utils';

import ConnectionsList from '@/components/connectors/ConnectionsList';
import { DynamicConnectionForm } from '@/components/connectors/DynamicConnectionForm';
import { ConnectorIcon } from '@/components/connectors/ConnectorIcon';
import { ConnectionUsageInstructionsModal } from '@/components/connectors/ConnectionUsageInstructionsModal';

import { useAuth } from '@/contexts/AuthContext';
import { apiClient, createAuthHeaders } from '@/lib/api-client';
import { projectsApi, type Project } from '@/lib/api/projects';
import { openOAuthPopup } from '@/lib/connector-oauth';

import type {
  Connection,
  ConnectionStatus,
  Connector,
  ConnectorListItem,
  ConnectorStatus,
  AuthMethod,
  UIField,
  ConnectionAccessResponse,
} from '@optidev/shared-types';
import { UI_FIELD_TYPES } from '@optidev/shared-types';

// Tabs configuration
const tabs = [
  { id: 'connectors', name: 'Connectors', icon: Plug, description: 'Browse available connectors' },
  { id: 'connections', name: 'Your Connections', icon: CheckCircle2, description: 'Your active connections' },
];

// Category display names (properly formatted)
const categoryDisplayNames: Record<string, string> = {
  all: 'Categories (All)',
  spreadsheet: 'Spreadsheets',
  analytics: 'Analytics',
  database: 'Databases',
  crm: 'CRM',
  payments: 'Payments',
  cloud: 'Cloud',
  ai: 'AI',
  development: 'Development',
  communication: 'Communication',
  productivity: 'Productivity',
  support: 'Support',
  marketing: 'Marketing',
  ecommerce: 'E-commerce',
};

// Connection status filter options (using enum values)
const connectionStatuses: Array<{ value: string; label: string }> = [
  { value: 'all', label: 'Status (All)' },
  { value: 'active', label: 'Active' },
  { value: 'error', label: 'Error' },
];

export default function ConnectorsFull() {
  // URL-based tab state
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const activeTab = searchParams.get('tab') || 'connectors';
  const { getIdToken } = useAuth();
  const { isMobile } = useResponsive();
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Sliding tab indicator state
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [sliderStyle, setSliderStyle] = useState({ left: 0, width: 0 });

  // Update slider position when active tab changes
  useLayoutEffect(() => {
    const activeButton = tabRefs.current.get(activeTab);
    const container = tabsContainerRef.current;
    if (activeButton && container) {
      const containerRect = container.getBoundingClientRect();
      const buttonRect = activeButton.getBoundingClientRect();
      setSliderStyle({
        left: buttonRect.left - containerRect.left,
        width: buttonRect.width,
      });
    }
  }, [activeTab]);

  // Connectors state (fetched from API)
  const [connectors, setConnectors] = useState<ConnectorListItem[]>([]);
  const [loadingConnectors, setLoadingConnectors] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [showConnectionModal, setShowConnectionModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Edit mode state
  const [editingConnection, setEditingConnection] = useState<Connection | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isReauthorizing, setIsReauthorizing] = useState(false);

  // OAuth + Resource Picker state (two-phase flow)
  const [oauthAccessToken, setOAuthAccessToken] = useState<string | null>(null);
  const [oauthConnectionId, setOAuthConnectionId] = useState<string | null>(null);

  // Pending requirement key for auto-selecting connection when opened from Remix modal
  const [pendingReqKey, setPendingReqKey] = useState<string | null>(null);

  // Helper to restore body scroll when modal closes
  const restoreBodyScroll = () => {
    const scrollY = document.body.dataset.scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.overflow = '';
    if (scrollY) {
      window.scrollTo(0, parseInt(scrollY, 10));
      delete document.body.dataset.scrollY;
    }
  };

  // Notify opener window and close tab if opened from another page (e.g., Remix modal)
  const notifyOpenerAndClose = (connectionId: string): boolean => {
    if (!pendingReqKey) return false;
    const opener = window.opener as Window & { onConnectionCreated?: (connectionId: string, reqKey: string) => void } | null;
    if (opener?.onConnectionCreated) {
      opener.onConnectionCreated(connectionId, pendingReqKey);
      setPendingReqKey(null); // Clear after use
      window.close();
      return true; // Tab will close, skip navigation
    }
    return false; // Normal flow, continue with navigation
  };

  // Usage modal state
  const [usageConnection, setUsageConnection] = useState<Connection | null>(null);

  // Connections state
  const [activeConnections, setActiveConnections] = useState<Connection[]>([]);
  const [loadingConnections, setLoadingConnections] = useState(false);

  // Projects state (for "Used by" display in connections list)
  const [projects, setProjects] = useState<Project[]>([]);

  // Search & Filter states - Connectors tab
  const [connectorSearch, setConnectorSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [connectorStatusFilter, setConnectorStatusFilter] = useState('all');

  // Search & Filter states - Connections tab
  const [connectionSearch, setConnectionSearch] = useState('');
  const [connectionCategoryFilter, setConnectionCategoryFilter] = useState('all');
  const [connectionStatusFilter, setConnectionStatusFilter] = useState('all');

  // Load connectors on mount (needed for icons in both tabs)
  useEffect(() => {
    loadConnectors();
  }, []);

  // Auto-open connector modal if 'connector' param is in URL
  // Used when redirecting from other pages (e.g., Remix modal "Create new connection")
  useEffect(() => {
    const connectorId = searchParams.get('connector');
    const reqKey = searchParams.get('reqKey');
    if (connectorId && connectors.length > 0 && !showConnectionModal) {
      // Check if connector exists
      const connectorExists = connectors.some(c => c.id === connectorId);
      if (connectorExists) {
        // Store reqKey for later use when connection is created (for auto-selecting the right dropdown)
        if (reqKey) {
          setPendingReqKey(reqKey);
        }
        // Clear the params from URL to prevent re-opening on refresh
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('connector');
        newParams.delete('reqKey');
        navigate(`/dashboard/connectors?${newParams.toString()}`, { replace: true });
        // Open the modal
        handleConnect(connectorId);
      }
    }
  }, [connectors, searchParams]);

  // Load connections when switching to connections tab
  useEffect(() => {
    if (activeTab === 'connections') {
      loadConnections();
    }
  }, [activeTab]);

  // Load connectors from API
  const loadConnectors = async (): Promise<void> => {
    setLoadingConnectors(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const { data } = await apiClient.get<ConnectorListItem[]>(
        '/api/connectors',
        { headers: createAuthHeaders(token) }
      );
      setConnectors(data || []);
    } catch (error) {
      console.error('Failed to load connectors:', error);
      toast.error('Failed to load connectors');
    } finally {
      setLoadingConnectors(false);
    }
  };

  // Load connections from API (also fetches projects for "Used by" display)
  const loadConnections = async (): Promise<void> => {
    setLoadingConnections(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      const [connectionsRes, projectsList] = await Promise.all([
        apiClient.get<Connection[]>('/api/connections', { headers: createAuthHeaders(token) }),
        projectsApi.getAll(token).catch(() => [] as Project[]),
      ]);
      setActiveConnections(connectionsRes.data || []);
      setProjects(projectsList);
    } catch (error) {
      console.error('Failed to load connections:', error);
      toast.error('Failed to load connections');
    } finally {
      setLoadingConnections(false);
    }
  };

  // Dynamic categories from fetched connectors
  const uniqueCategories = useMemo(() => {
    const cats = new Set(connectors.map(c => c.category).filter(Boolean));
    return ['all', ...Array.from(cats)];
  }, [connectors]);

  // Featured connector IDs (shown first, in this order)
  const featuredConnectorIds = [
    'google-sheets',
    'microsoft-excel',
    'outlook-calendar',
    'google-calendar',
    'stripe',
    'openai',
    'postgresql',
    'mysql',
    'mongodb',
  ];

  // Filtered and separated connectors (available vs coming soon)
  const { availableConnectors, comingSoonConnectors } = useMemo(() => {
    const filtered = connectors.filter(connector => {
      // Search filter
      if (connectorSearch) {
        const search = connectorSearch.toLowerCase();
        if (!connector.name.toLowerCase().includes(search) &&
            !(connector.description?.toLowerCase().includes(search))) {
          return false;
        }
      }
      // Category filter
      if (categoryFilter !== 'all' && connector.category !== categoryFilter) {
        return false;
      }
      // Status filter (only apply if not 'all')
      if (connectorStatusFilter !== 'all' && connector.status !== connectorStatusFilter) {
        return false;
      }
      return true;
    });

    // Separate available and coming soon
    const available = filtered
      .filter(c => c.status === 'available')
      .sort((a, b) => {
        // Featured connectors first
        const aFeatured = featuredConnectorIds.indexOf(a.id);
        const bFeatured = featuredConnectorIds.indexOf(b.id);
        if (aFeatured !== -1 && bFeatured === -1) return -1;
        if (aFeatured === -1 && bFeatured !== -1) return 1;
        if (aFeatured !== -1 && bFeatured !== -1) return aFeatured - bFeatured;
        // Then alphabetically by name
        return a.name.localeCompare(b.name);
      });

    const comingSoon = filtered
      .filter(c => c.status === 'coming_soon')
      .sort((a, b) => a.name.localeCompare(b.name));

    return { availableConnectors: available, comingSoonConnectors: comingSoon };
  }, [connectors, connectorSearch, categoryFilter, connectorStatusFilter]);

  // Filtered connections
  const filteredConnections = useMemo(() => {
    return activeConnections.filter(connection => {
      // Search filter
      if (connectionSearch) {
        const search = connectionSearch.toLowerCase();
        if (!connection.name.toLowerCase().includes(search) &&
            !(connection.type?.toLowerCase().includes(search))) {
          return false;
        }
      }
      // Category filter
      if (connectionCategoryFilter !== 'all') {
        const connector = connectors.find(c => c.id === connection.type);
        if (connector?.category !== connectionCategoryFilter) {
          return false;
        }
      }
      // Status filter
      if (connectionStatusFilter !== 'all' && connection.status !== connectionStatusFilter) {
        return false;
      }
      return true;
    });
  }, [activeConnections, connectors, connectionSearch, connectionCategoryFilter, connectionStatusFilter]);

  // Unique categories from user's connections (for filter dropdown)
  const uniqueConnectionCategories = useMemo(() => {
    const categories = new Set<string>();
    activeConnections.forEach(connection => {
      const connector = connectors.find(c => c.id === connection.type);
      if (connector?.category) {
        categories.add(connector.category);
      }
    });
    return ['all', ...Array.from(categories)];
  }, [activeConnections, connectors]);

  // Check if connector has resource picker in its OAuth auth method
  const connectorHasResourcePicker = useMemo(() => {
    if (!selectedConnector?.uiSchema?.authMethods) return false;
    const oauthMethod = selectedConnector.uiSchema.authMethods.find(
      (m: AuthMethod) => m.id === 'oauth' || m.id === 'oauth2'
    );
    if (!oauthMethod) return false;
    // Check if has resource picker field or resourceScope is 'selected'
    const hasPickerField = oauthMethod.fields?.some(
      (f: UIField) => f.type === UI_FIELD_TYPES.RESOURCE_PICKER
    );
    return hasPickerField || oauthMethod.resourceScope === 'selected';
  }, [selectedConnector]);

  // Handle connector card click - fetch full details and show modal
  const handleConnect = async (connectorId: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) return;

      // Fetch full connector details (includes uiSchema)
      const { data } = await apiClient.get<Connector>(
        `/api/connectors/${connectorId}`,
        { headers: createAuthHeaders(token) }
      );
      setSelectedConnector(data);
      setShowConnectionModal(true);
    } catch (error) {
      console.error('Failed to load connector details:', error);
      toast.error('Failed to load connector');
    }
  };

  // Handle connection creation from DynamicConnectionForm
  const handleCreateConnection = async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }): Promise<void> => {
    setIsCreating(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      const { data: createdConnection } = await apiClient.post<Connection>('/api/connections', {
        type: selectedConnector!.id,
        ...formData,
      }, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection created successfully');
      restoreBodyScroll();
      setShowConnectionModal(false);
      setSelectedConnector(null);
      // Refresh connections list then navigate to connections tab
      await loadConnections().catch(console.error);
      if (!notifyOpenerAndClose(createdConnection.id)) {
        navigate('/dashboard/connectors?tab=connections');
      }
    } catch (error: unknown) {
      console.error('Failed to create connection:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to create connection');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle OAuth start for connectors that use OAuth (opens in popup)
  const handleOAuthStart = async (authMethod: string, connectionName?: string, additionalParams?: Record<string, string>): Promise<void> => {
    if (!selectedConnector) return;

    try {
      const token = await getIdToken();
      if (!token) return;

      // Get auth URL from backend (pass authMethod for scope overrides)
      const { data } = await apiClient.post<{ authUrl: string }>(
        `/api/connections/oauth/init`,
        {
          connectorId: selectedConnector.id,
          authMethod, // Pass the selected auth method (e.g., 'oauth', 'oauth_readonly')
          connectionName, // Pass user-typed name so OAuth connection uses it instead of auto-generated
          additionalParams, // Pre-OAuth fields (e.g., shopDomain for Shopify)
        },
        { headers: createAuthHeaders(token) }
      );

      if (data.authUrl) {
        // Open OAuth in popup
        const result = await openOAuthPopup(data.authUrl);

        if (result.success && result.connectionId) {
          // Check if connector has resource picker
          if (connectorHasResourcePicker) {
            // Two-phase flow: OAuth done, now show picker
            try {
              // Fetch access token from the new connection
              const { data: accessData } = await apiClient.post<ConnectionAccessResponse>(
                `/api/connections/${result.connectionId}/access`,
                {},
                { headers: createAuthHeaders(token) }
              );

              if (accessData.data?.accessToken) {
                // Store token and connection ID for the picker
                setOAuthAccessToken(accessData.data.accessToken as string);
                setOAuthConnectionId(result.connectionId);
                toast.success('Connected! Now select your files.');
                // Modal stays open, form will show the picker
              } else {
                // No access token returned, fall back to normal flow
                toast.success(`${result.connectorName || selectedConnector.name} connected successfully`);
                restoreBodyScroll();
                setShowConnectionModal(false);
                setSelectedConnector(null);
                await loadConnections().catch(console.error);
                if (!notifyOpenerAndClose(result.connectionId)) {
                  navigate('/dashboard/connectors?tab=connections');
                }
              }
            } catch (accessError) {
              console.error('Failed to get access token:', accessError);
              toast.success(`${result.connectorName || selectedConnector.name} connected (picker unavailable)`);
              restoreBodyScroll();
              setShowConnectionModal(false);
              setSelectedConnector(null);
              await loadConnections().catch(console.error);
              if (!notifyOpenerAndClose(result.connectionId)) {
                navigate('/dashboard/connectors?tab=connections');
              }
            }
          } else {
            // No resource picker - normal flow
            toast.success(`${result.connectorName || selectedConnector.name} connected successfully`);
            restoreBodyScroll();
            setShowConnectionModal(false);
            setSelectedConnector(null);
            await loadConnections().catch(console.error);
            if (!notifyOpenerAndClose(result.connectionId)) {
              navigate('/dashboard/connectors?tab=connections');
            }
          }
        } else if (result.error && result.error !== 'Authentication cancelled') {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('OAuth init error:', error);
      toast.error('Failed to start OAuth flow');
    }
  };

  // Handle edit connection - fetch connector and show edit modal
  const handleEditConnection = async (connection: Connection): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) return;

      // Fetch full connector details for the form schema
      const { data: connector } = await apiClient.get<Connector>(
        `/api/connectors/${connection.type}`,
        { headers: createAuthHeaders(token) }
      );

      // Note: We don't fetch access token eagerly anymore.
      // The GooglePicker component will fetch it lazily when the user clicks "Add More"
      // using the connectionId and getAuthToken props.

      setSelectedConnector(connector);
      setEditingConnection(connection);
      setShowConnectionModal(true);
    } catch (error) {
      console.error('Failed to load connector details:', error);
      toast.error('Failed to load connector details');
    }
  };

  // Handle update connection
  const handleUpdateConnection = async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }): Promise<void> => {
    if (!editingConnection) return;

    setIsEditing(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      // Extract selectedResources from credentials - it should be stored in metadata
      const { selectedResources, ...otherCredentials } = formData.credentials;

      // Build update payload
      const updatePayload: Record<string, unknown> = {
        name: formData.name,
      };

      // Only send credentials if there are other fields besides selectedResources
      if (Object.keys(otherCredentials).length > 0) {
        updatePayload.credentials = otherCredentials;
      }

      // Store selectedResources in metadata (not credentials - it's display data)
      if (selectedResources) {
        updatePayload.metadata = {
          ...(editingConnection.metadata as Record<string, unknown> || {}),
          selectedResources,
        };
      }

      await apiClient.patch(`/api/connections/${editingConnection.id}`, updatePayload, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection updated successfully');
      restoreBodyScroll();
      setShowConnectionModal(false);
      setSelectedConnector(null);
      setEditingConnection(null);
      setOAuthAccessToken(null); // Clear access token
      loadConnections();
    } catch (error: unknown) {
      console.error('Failed to update connection:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to update connection');
    } finally {
      setIsEditing(false);
    }
  };

  // Handle update connection with selected resources (after OAuth + picker)
  const handleUpdateConnectionWithResources = async (formData: {
    name: string;
    authMethod?: string;
    credentials: Record<string, unknown>;
  }): Promise<void> => {
    if (!oauthConnectionId) return;

    setIsCreating(true);
    try {
      const token = await getIdToken();
      if (!token) throw new Error('Authentication required');

      // Update the existing connection with selected resources in metadata (not credentials)
      // selectedResources is display data, not sensitive - store in metadata for faster access
      // Don't send name - preserve the auto-generated name from OAuth (e.g., "Google Sheets (email@example.com)")
      await apiClient.patch(`/api/connections/${oauthConnectionId}`, {
        metadata: {
          selectedResources: formData.credentials.selectedResources,
        },
      }, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection saved with selected files');
      restoreBodyScroll();
      setShowConnectionModal(false);
      const connectionId = oauthConnectionId;
      setSelectedConnector(null);
      setOAuthAccessToken(null);
      setOAuthConnectionId(null);
      // Refresh connections list then navigate to connections tab
      await loadConnections().catch(console.error);
      if (!notifyOpenerAndClose(connectionId)) {
        navigate('/dashboard/connectors?tab=connections');
      }
    } catch (error: unknown) {
      console.error('Failed to update connection:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Failed to save connection');
    } finally {
      setIsCreating(false);
    }
  };

  // Handle refresh OAuth token
  const handleRefreshToken = async (id: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.post(`/api/connections/${id}/refresh`, {}, {
        headers: createAuthHeaders(token),
      });

      toast.success('Token refreshed successfully');
      loadConnections();
    } catch (error) {
      console.error('Refresh token error:', error);
      toast.error('Failed to refresh token');
    }
  };

  // Handle toggle enabled
  const handleToggleEnabled = async (id: string, enabled: boolean): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.patch(`/api/connections/${id}`, { enabled }, {
        headers: createAuthHeaders(token),
      });

      loadConnections();
    } catch (error) {
      console.error('Toggle enabled error:', error);
      throw error; // Let the caller handle the error toast
    }
  };

  // Handle view usage (show code examples modal)
  const handleViewUsage = (connection: Connection): void => {
    setUsageConnection(connection);
  };

  // Handle reauthorize OAuth connection from edit modal
  const handleReauthorizeFromModal = async (): Promise<void> => {
    if (!editingConnection) return;

    setIsReauthorizing(true);
    try {
      const token = await getIdToken();
      if (!token) return;

      // Get reauth URL from backend
      const { data } = await apiClient.post<{ authUrl: string }>(
        `/api/connections/${editingConnection.id}/reauthorize`,
        {},
        { headers: createAuthHeaders(token) }
      );

      if (data.authUrl) {
        // Open OAuth in popup
        const result = await openOAuthPopup(data.authUrl);

        if (result.success) {
          toast.success(`${editingConnection.name} reauthorized successfully`);
          // Close modal and refresh connections list
          restoreBodyScroll();
          setShowConnectionModal(false);
          setSelectedConnector(null);
          setEditingConnection(null);
          loadConnections().catch(console.error);
        } else if (result.error && result.error !== 'Authentication cancelled') {
          toast.error(result.error);
        }
      }
    } catch (error) {
      console.error('Reauthorization error:', error);
      toast.error('Failed to start reauthorization');
    } finally {
      setIsReauthorizing(false);
    }
  };

  // Handle delete connection
  const handleDeleteConnection = async (id: string): Promise<void> => {
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await apiClient.delete(`/api/connections/${id}`, {
        headers: createAuthHeaders(token),
      });

      toast.success('Connection deleted successfully');
      loadConnections();
    } catch (error) {
      console.error('Delete connection error:', error);
      toast.error('Failed to delete connection');
    }
  };

  // Handle test connection
  const handleTestConnection = async (id: string): Promise<void> => {
    const toastId = toast.loading('Testing connection...');

    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Authentication required', { id: toastId });
        return;
      }

      const { data } = await apiClient.post<{ success: boolean; message?: string }>(
        `/api/connections/${id}/test`,
        {},
        { headers: createAuthHeaders(token) }
      );

      if (data.success) {
        toast.success(data.message || 'Connection test successful!', { id: toastId });
      } else {
        toast.error(data.message || 'Connection test failed', { id: toastId });
      }
      loadConnections();
    } catch (error) {
      console.error('Test connection error:', error);
      const axiosError = error as { response?: { data?: { message?: string } } };
      toast.error(axiosError.response?.data?.message || 'Connection test failed', { id: toastId });
    }
  };

  return (
    <div className="min-h-full">
      <div className={cn(
        "max-w-[1400px] mx-auto",
        isMobile ? "px-4 py-6" : "px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-10"
      )}>
        {/* Header */}
        <div className={cn(isMobile ? "mb-5" : "mb-10")}>
          <h1 className={cn(
            "font-semibold tracking-tight text-foreground",
            isMobile ? "text-2xl mb-2" : "text-2xl sm:text-3xl mb-2"
          )}>Data Connectors</h1>
          <p className={cn(
            "text-muted-foreground",
            isMobile ? "text-sm" : "text-sm sm:text-base"
          )}>
            Connect your data sources to unlock powerful analytics and AI capabilities
          </p>
        </div>

        {/* Mobile: Tabs first, then search/filter row */}
        {isMobile ? (
          <div className="space-y-3 mb-4">
            {/* Tabs - full width on mobile */}
            <div
              ref={tabsContainerRef}
              className="relative flex p-1 bg-muted/60 dark:bg-muted/40 rounded-lg border border-border/40"
            >
              {/* Sliding background indicator */}
              <div
                className="absolute top-1 bg-background dark:bg-muted rounded-md border border-border/50 dark:border-border transition-all duration-300 ease-out"
                style={{
                  left: sliderStyle.left,
                  width: sliderStyle.width,
                  height: 'calc(100% - 8px)',
                }}
              />
              {tabs.map((tabItem) => (
                <button
                  key={tabItem.id}
                  ref={(el) => {
                    if (el) tabRefs.current.set(tabItem.id, el);
                  }}
                  onClick={() => navigate(`/dashboard/connectors?tab=${tabItem.id}`)}
                  className={cn(
                    "relative z-10 flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors duration-200",
                    activeTab === tabItem.id
                      ? "text-foreground"
                      : "text-muted-foreground"
                  )}
                >
                  <tabItem.icon className="h-4 w-4" />
                  <span className="truncate">{tabItem.name}</span>
                </button>
              ))}
            </div>

            {/* Search + Filter button row */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={activeTab === 'connectors' ? "Search..." : "Search..."}
                  value={activeTab === 'connectors' ? connectorSearch : connectionSearch}
                  onChange={(e) => activeTab === 'connectors'
                    ? setConnectorSearch(e.target.value)
                    : setConnectionSearch(e.target.value)
                  }
                  className="pl-9 h-9 text-sm"
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                className="h-9 px-3 shrink-0"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>

            {/* Mobile filters dropdown */}
            {showMobileFilters && (
              <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 scrollbar-hide">
                {activeTab === 'connectors' ? (
                  uniqueCategories.length > 1 && (
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 shrink-0">
                        <SelectValue placeholder="Category" />
                      </SelectTrigger>
                      <SelectContent>
                        {uniqueCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>
                            {categoryDisplayNames[cat] || cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )
                ) : (
                  <>
                    {uniqueConnectionCategories.length > 1 && (
                      <Select value={connectionCategoryFilter} onValueChange={setConnectionCategoryFilter}>
                        <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 shrink-0">
                          <SelectValue placeholder="Category" />
                        </SelectTrigger>
                        <SelectContent>
                          {uniqueConnectionCategories.map(cat => (
                            <SelectItem key={cat} value={cat}>
                              {categoryDisplayNames[cat] || cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Select value={connectionStatusFilter} onValueChange={setConnectionStatusFilter}>
                      <SelectTrigger className="h-8 text-xs w-auto min-w-fit px-2.5 shrink-0">
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        {connectionStatuses.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </>
                )}
              </div>
            )}
          </div>
        ) : (
          /* Desktop layout */
          <div className="mb-6 space-y-4">
            {/* Pills + Search */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center h-11 p-1.5 bg-accent dark:bg-muted rounded-xl gap-0.5">
                {tabs.map((tabItem) => (
                  <button
                    key={tabItem.id}
                    ref={(el) => {
                      if (el) tabRefs.current.set(tabItem.id, el);
                    }}
                    onClick={() => navigate(`/dashboard/connectors?tab=${tabItem.id}`)}
                    className={cn(
                      "px-3 h-full text-sm font-medium rounded-md transition-colors",
                      activeTab === tabItem.id
                        ? "bg-foreground/90 text-background shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {tabItem.name}
                  </button>
                ))}
              </div>
              <div className="relative w-56">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={activeTab === 'connectors' ? "Search connectors..." : "Search connections..."}
                  value={activeTab === 'connectors' ? connectorSearch : connectionSearch}
                  onChange={(e) => activeTab === 'connectors'
                    ? setConnectorSearch(e.target.value)
                    : setConnectionSearch(e.target.value)
                  }
                  className="pl-10"
                />
              </div>
            </div>

          </div>
        )}

        {/* Main Content */}
        <div>
            {activeTab === 'connectors' && (
              <div className="space-y-6">
                {/* Connectors Grid */}
                {loadingConnectors ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="mt-2 text-muted-foreground">Loading connectors...</p>
                  </div>
                ) : availableConnectors.length === 0 && comingSoonConnectors.length === 0 ? (
                  <div className="text-center py-12">
                    <Plug className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">
                      {connectors.length === 0
                        ? 'No connectors available.'
                        : 'No connectors found matching your filters.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {/* Available Connectors */}
                    {availableConnectors.length > 0 && (
                      <div className="space-y-6">
                        {(() => {
                          const grouped = availableConnectors.reduce<Record<string, typeof availableConnectors>>((acc, c) => {
                            const cat = c.category || 'other';
                            if (!acc[cat]) acc[cat] = [];
                            acc[cat].push(c);
                            return acc;
                          }, {});
                          return Object.entries(grouped).map(([category, items]) => (
                            <div key={category} className="rounded-xl bg-white dark:bg-card border border-border/50 p-4 sm:p-5">
                              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 px-1">
                                {categoryDisplayNames[category] || category}
                              </h3>
                              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {items.map((connector) => (
                                  <div
                                    key={connector.id}
                                    className="flex items-center gap-3.5 p-4 rounded-lg bg-muted/50 dark:bg-white/[0.03] border border-border cursor-pointer hover:border-primary/30 transition-colors"
                                    onClick={() => handleConnect(connector.id)}
                                  >
                                    <ConnectorIcon connector={connector} className="h-8 w-8 flex-shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <h3 className="font-medium text-sm text-foreground">{connector.name}</h3>
                                      <p className="text-xs text-muted-foreground truncate mt-0.5">{connector.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}

                    {/* Coming Soon Section */}
                    {comingSoonConnectors.length > 0 && connectorStatusFilter !== 'available' && (
                      <>
                        <div className="relative">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-background px-2 text-muted-foreground">
                              Coming Soon
                            </span>
                          </div>
                        </div>

                        <div className="rounded-xl bg-white dark:bg-card border border-border/50 p-4 sm:p-5 opacity-60">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {comingSoonConnectors.map((connector) => (
                              <div
                                key={connector.id}
                                className="flex items-center gap-3.5 p-4 rounded-lg bg-muted/50 dark:bg-white/[0.03] border border-border"
                              >
                                <ConnectorIcon connector={connector} className="h-8 w-8 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <h3 className="font-medium text-sm text-foreground">{connector.name}</h3>
                                  <p className="text-xs text-muted-foreground truncate mt-0.5">{connector.description}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'connections' && (
              <div className="space-y-6">
                {/* Connections List */}
                {loadingConnections ? (
                  <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="mt-2 text-muted-foreground">Loading connections...</p>
                  </div>
                ) : filteredConnections.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground text-sm mb-4">
                      {activeConnections.length === 0
                        ? "No connections yet. Connect a data source to get started."
                        : "No connections found matching your filters."}
                    </p>
                    {activeConnections.length === 0 && (
                      <Button
                        onClick={() => navigate('/dashboard/connectors?tab=connectors')}
                        variant="outline"
                        size="sm"
                      >
                        Browse Connectors
                      </Button>
                    )}
                  </div>
                ) : (
                  <ConnectionsList
                    connections={filteredConnections}
                    connectors={connectors}
                    projects={projects}
                    onEdit={handleEditConnection}
                    onDelete={handleDeleteConnection}
                    onTest={handleTestConnection}
                    onRefreshToken={handleRefreshToken}
                    onToggleEnabled={handleToggleEnabled}
                    onViewUsage={handleViewUsage}
                    onAdd={() => navigate('/dashboard/connectors?tab=connectors')}
                  />
                )}
              </div>
            )}
        </div>

        {/* Dynamic Connection Modal (Create or Edit) */}
        {showConnectionModal && selectedConnector && createPortal(
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[9999] p-4 overflow-hidden"
            ref={(el) => {
              // Lock body scroll and position when modal opens
              // This prevents Google Picker from calculating wrong height
              if (el) {
                const scrollY = window.scrollY;
                document.body.style.position = 'fixed';
                document.body.style.top = `-${scrollY}px`;
                document.body.style.left = '0';
                document.body.style.right = '0';
                document.body.style.overflow = 'hidden';
                // Store scroll position for restoration
                document.body.dataset.scrollY = String(scrollY);
              }
            }}
          >
            <div
              className="absolute inset-0"
              onClick={() => {
                restoreBodyScroll();
                setShowConnectionModal(false);
                setSelectedConnector(null);
                setEditingConnection(null);
                setOAuthAccessToken(null);
                setOAuthConnectionId(null);
              }}
            />
            <div className={cn(
              "relative bg-card shadow-xl w-full overflow-y-auto",
              isMobile
                ? "max-w-none max-h-[85dvh] rounded-t-2xl mt-auto"
                : "max-w-lg max-h-[90vh] rounded-lg"
            )}>
              <button
                onClick={() => {
                  restoreBodyScroll();
                  setShowConnectionModal(false);
                  setSelectedConnector(null);
                  setEditingConnection(null);
                  setOAuthAccessToken(null);
                  setOAuthConnectionId(null);
                }}
                className="absolute right-4 top-4 p-1.5 hover:bg-accent rounded-lg transition-colors z-10"
              >
                <X className="h-5 w-5 text-muted-foreground" />
              </button>
              <div className="p-6">
                <DynamicConnectionForm
                  key={editingConnection?.id || oauthConnectionId || 'new'}
                  connector={selectedConnector}
                  onSubmit={oauthConnectionId ? handleUpdateConnectionWithResources : (editingConnection ? handleUpdateConnection : handleCreateConnection)}
                  onCancel={() => {
                    restoreBodyScroll();
                    setShowConnectionModal(false);
                    setSelectedConnector(null);
                    setEditingConnection(null);
                    setOAuthAccessToken(null);
                    setOAuthConnectionId(null);
                  }}
                  onOAuthStart={handleOAuthStart}
                  onReauthorize={handleReauthorizeFromModal}
                  isSubmitting={editingConnection ? isEditing : isCreating}
                  isReauthorizing={isReauthorizing}
                  isEditMode={!!editingConnection}
                  authorizedEmail={
                    (editingConnection?.metadata as Record<string, unknown>)?.email as string ||
                    (editingConnection?.metadata as Record<string, unknown>)?.name as string
                  }
                  initialData={editingConnection ? {
                    name: editingConnection.name,
                    authMethod: editingConnection.authMethod,
                    // Read selectedResources from metadata first, fall back to credentials for backward compatibility
                    credentials: {
                      selectedResources:
                        (editingConnection.metadata as Record<string, unknown>)?.selectedResources ||
                        (editingConnection.credentials as Record<string, unknown>)?.selectedResources,
                    },
                    maskedCredentials: editingConnection.credentials, // For placeholder display
                  } : undefined}
                  accessToken={oauthAccessToken ?? undefined}
                  connectionId={oauthConnectionId || editingConnection?.id}
                  getAuthToken={getIdToken}
                />
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Connection Usage Instructions Modal */}
        <ConnectionUsageInstructionsModal
          connection={usageConnection}
          connector={usageConnection ? connectors.find(c => c.id === usageConnection.type) : undefined}
          isOpen={!!usageConnection}
          onClose={() => setUsageConnection(null)}
        />
      </div>
    </div>
  );
}
